/**
 * 法务静态页固定路径（dev-docs `01-通用约定/60-环境与域名.md` §8）—— admin 侧唯一登记点。
 *
 * §8 把四页法务页的路径钉死在各 app 品牌域名根下，并写明「外链法务页一律按本节表中的固定路径写，
 * 不要另造路径」：曾有 FAQ 外链用了四个自造路径（`/legal-docs/{terms-of-use,privacy-policy,…}`），
 * 线上全部 404。admin 手里有两个运营手填外链的承载面 —— FAQ 答案正文与投放落地页产品的页脚法务链接，
 * 本文件给这两处提供同一份路径表与判定。
 *
 * **只判 path、不判 host**：品牌域名按 app 各异（§8 品牌域名表），admin 不持有该表；而事故的形态
 * 恰恰是 path 自造。**只做提示、不拒写入**：落地页字段的 API 契约（`02-API契约/AE-投放落地页.md`）
 * 只要求 https URL，服务端硬拒要先改契约；FAQ 正文是自由内容，可能合法地引用第三方条款。
 */

export const LEGAL_PAGE_PATHS = {
  privacy_policy: '/privacy-policy.html',
  user_agreement: '/user-agreement.html',
  data_deletion: '/data-deletion.html',
  cookie_notice: '/cookie-notice.html',
} as const;

const LEGAL_PAGE_PATH_SET: ReadonlySet<string> = new Set(Object.values(LEGAL_PAGE_PATHS));

/**
 * 像法务页的 path：命中这些词但不在固定路径表内，就是「另造路径」的嫌疑。
 * - `delet` 取词干：`delete-account` 与 `account-deletion` 都要命中 —— 数据删除页正是 Facebook /
 *   Google 合规后台要填的那个 URL，漏报代价最高。
 * - `tos` 按词边界匹配，否则 `/photos/…` 会误报；`eula` / `sdk` 对应 §8「Cookie 与 SDK 说明」一类自造形态。
 */
const LEGAL_LOOKING_PATH_RE =
  /privacy|terms|agreement|cookie|delet|legal|policy|eula|sdk|(?:^|[^a-z])tos(?:[^a-z]|$)/i;

/**
 * 文本里的 http(s) URL，按**白名单字符集**截断：只收 URL 里实际会出现的 ASCII 字符，遇到反引号、
 * `*`、引号（含弯引号 / „“ / «»）、括号与任何全角字符即止。
 * 早先按黑名单（空白与少数引号括号）截断，`` `…html` `` / `**…html**` / `„…html“` / `（…html）`
 * 会把紧贴的标记字符并进 path，正确的固定路径反被报成自造路径。scheme 不区分大小写。
 */
const URL_IN_TEXT_RE = /https?:\/\/[A-Za-z0-9\-._~:/?#@!$&+,;=%]+/gi;

/**
 * 取 URL 的 path（不含 query / fragment）；不是 `scheme://host…` 形态返回 null。
 * 用正则而非 `new URL()`：本包 tsconfig 无 DOM lib（server 与 web 共用、零 runtime 依赖），`URL` 编不过
 * —— 与 `landing-pages.ts` 的 `HttpsUrlSchema` 同一取舍。host 段要求非空，`https://` 这类半截输入不算 URL。
 */
const URL_PARTS_RE = /^https?:\/\/[^/?#\s]+(\/[^?#\s]*)?(?:[?#]\S*)?$/i;

function pathnameOf(url: string): string | null {
  const m = URL_PARTS_RE.exec(url);
  if (!m) return null;
  return m[1] ?? '/';
}

/**
 * 单个法务链接字段：URL 可解析、但 path 不等于该字段应有的固定路径 → true。
 * 解析不了（空串 / 半截输入）返回 false —— 格式错由字段自己的 https 校验报，这里不重复报。
 */
export function isLegalUrlPathMismatch(url: string, expectedPath: string): boolean {
  const pathname = pathnameOf(url.trim());
  return pathname !== null && pathname !== expectedPath;
}

/**
 * 自由文本（FAQ 答案 markdown）里「像法务页、但 path 不在固定路径表」的外链，去重后按出现顺序返回。
 * 不像法务页的外链（帮助中心、第三方站点首页等）不报 —— 这里只抓 §8 那类事故。
 */
export function findNonCanonicalLegalLinks(text: string): string[] {
  const found: string[] = [];
  for (const raw of text.match(URL_IN_TEXT_RE) ?? []) {
    // 句末标点与 markdown 强调 / 删除线的 `_` `~` 会被白名单字符集收进来（它们本身是合法 URL 字符），
    // 剥掉再判，免得正确路径被误报
    const url = raw.replace(/[.,;:!?_~]+$/, '');
    const pathname = pathnameOf(url);
    if (pathname === null || !LEGAL_LOOKING_PATH_RE.test(pathname)) continue;
    if (LEGAL_PAGE_PATH_SET.has(pathname)) continue;
    if (!found.includes(url)) found.push(url);
  }
  return found;
}
